/**
 * CaptureSheet Component
 *
 * Renders the capture flow inside a BottomSheet on mobile.
 * On desktop, falls back to the existing CaptureModal (Dialog).
 * This ensures mobile gets a native-feel drag-dismiss sheet,
 * while desktop keeps the centered dialog experience.
 *
 * Requirements: Phase 2 — Native Feel
 */

import { BottomSheet } from '@/components/ui/BottomSheet';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { CaptureModal } from './CaptureModal';

interface CaptureSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CaptureSheet({ open, onOpenChange }: CaptureSheetProps) {
  const isMobile = useIsMobile();

  // On desktop, use the existing Dialog-based modal
  if (!isMobile) {
    return <CaptureModal open={open} onOpenChange={onOpenChange} />;
  }

  // On mobile, render in a bottom sheet
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title="Capture" expandable>
      <CaptureSheetBody onDone={() => onOpenChange(false)} />
    </BottomSheet>
  );
}

/**
 * Lightweight mobile capture body — renders the core CaptureModal inside
 * the sheet context. Since CaptureModal's entire logic is self-contained
 * and uses Dialog internally, we render it in a "headless" mode by passing
 * open=true so its Dialog opens immediately (stacked inside our sheet).
 *
 * Alternative: We render the CaptureModal which will show its Dialog UI within
 * the BottomSheet. The Dialog overlay will be suppressed because it's already
 * within the sheet's portal.
 *
 * For now, the simplest approach: render CaptureModal with open=true;
 * it manages its own state. The BottomSheet provides the mobile chrome.
 *
 * NOTE: A cleaner future refactor would extract CaptureModal's body into
 * a <CaptureForm> component. For Phase 2, this "sheet wrapping modal" pattern
 * works and avoids a risky 930-line refactor.
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { cn } from '@/lib/utils';
import * as ingestAPI from '@/api/ingest';
import * as knowledgeAPI from '@/api/knowledge';
import { queryKeys } from '@/lib/queryKeys';
import { useChatStore } from '@/store/chatStore';
import { useUIStore } from '@/store/uiStore';
import { VoiceCapture } from './VoiceCapture';
import type { IngestTextResponse, IngestURLResponse, NotesReviewResponse } from '@/types/api';

const MAX_BULK_URLS = 100;
const URL_REGEX = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/gi;

interface CaptureSheetBodyProps {
  onDone: () => void;
}

function CaptureSheetBody({ onDone }: CaptureSheetBodyProps) {
  const queryClient = useQueryClient();
  const currentSessionId = useChatStore((state) => state.currentSessionId);
  const captureInitialTab = useUIStore((state) => state.captureInitialTab);
  const captureVoiceRequestId = useUIStore((state) => state.captureVoiceRequestId);
  const [activeTab, setActiveTab] = React.useState<'text' | 'file' | 'url' | 'notes' | 'voice'>('notes');

  React.useEffect(() => {
    setActiveTab(captureInitialTab);
  }, [captureInitialTab]);

  const [textInput, setTextInput] = React.useState('');
  const [urlInput, setUrlInput] = React.useState('');
  const [urlError, setUrlError] = React.useState<string | null>(null);
  const [notesInput, setNotesInput] = React.useState('');
  const [notesFile, setNotesFile] = React.useState<File | null>(null);
  const [notesError, setNotesError] = React.useState<string | null>(null);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [processingItemId, setProcessingItemId] = React.useState<string | null>(null);

  const textMutation = useMutation({
    mutationFn: (text: string) => ingestAPI.text(text, 'paste'),
    onSuccess: (_data: IngestTextResponse) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.all });
      setTextInput('');
      onDone();
    },
  });

  const fileMutation = useMutation({
    mutationFn: (file: File) => ingestAPI.file(file),
    onSuccess: (data: IngestURLResponse) => {
      setProcessingItemId(data.item_id);
      setSelectedFile(null);
    },
  });

  const notesReviewMutation = useMutation({
    mutationFn: () => ingestAPI.notesReview({
      text: notesInput,
      file: notesFile || undefined,
      chatSessionId: currentSessionId,
    }),
    onSuccess: (data: NotesReviewResponse) => {
      setNotesInput('');
      setNotesFile(null);
      onDone();
      window.location.hash = '#/';
      window.dispatchEvent(new CustomEvent('notes-review-ready', {
        detail: { sessionId: data.session_id },
      }));
    },
    onError: (error: Error) => {
      setNotesError(error.message || 'Could not start notes review');
    },
  });

  const normalizeUrl = React.useCallback((url: string) => url.trim().replace(/[.,)]$/, ''), []);

  const parseUrls = React.useCallback((text: string): string[] => {
    const matches = text.match(URL_REGEX) || [];
    const seen = new Set<string>();
    return matches
      .map(normalizeUrl)
      .filter((url) => {
        const key = url.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [normalizeUrl]);

  const bulkUrlMutation = useMutation({
    mutationFn: (urls: string[]) => ingestAPI.bulkUrls(urls),
    onSuccess: () => {
      setUrlInput('');
      setUrlError(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.all });
      onDone();
    },
    onError: (error: Error) => {
      setUrlError(error.message || 'Failed to queue URLs');
    },
  });

  const { data: statusData } = useQuery({
    queryKey: ['itemStatus', processingItemId],
    queryFn: () => knowledgeAPI.getStatus(processingItemId!),
    enabled: !!processingItemId,
    refetchInterval: (query) => {
      if (query.state.data?.status === 'ready' || query.state.data?.status === 'failed') return false;
      return 5000;
    },
  });

  React.useEffect(() => {
    if (statusData?.status === 'ready') {
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.all });
      setTimeout(() => { setProcessingItemId(null); onDone(); }, 1500);
    } else if (statusData?.status === 'failed') {
      setTimeout(() => setProcessingItemId(null), 3000);
    }
  }, [statusData, queryClient, onDone]);

  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (textInput.trim()) textMutation.mutate(textInput.trim());
  };

  const handleUrlSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const urls = parseUrls(urlInput);
    if (urls.length === 0) { setUrlError('Paste at least one URL.'); return; }
    if (urls.length > MAX_BULK_URLS) { setUrlError(`Max ${MAX_BULK_URLS} URLs at a time.`); return; }
    setUrlError(null);
    bulkUrlMutation.mutate(urls);
  };

  const handleNotesSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!notesInput.trim() && !notesFile) { setNotesError('Paste notes or attach a file.'); return; }
    setNotesError(null);
    notesReviewMutation.mutate();
  };

  const handleFileUpload = () => { if (selectedFile) fileMutation.mutate(selectedFile); };

  const handleDragEnter = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) setSelectedFile(files[0]);
  };
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) setSelectedFile(files[0]);
  };

  return (
    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
      <TabsList className="grid w-full grid-cols-5">
        <TabsTrigger value="notes">Notes</TabsTrigger>
        <TabsTrigger value="text">Text</TabsTrigger>
        <TabsTrigger value="url">URLs</TabsTrigger>
        <TabsTrigger value="file">File</TabsTrigger>
        <TabsTrigger value="voice">Voice</TabsTrigger>
      </TabsList>

      <TabsContent value="voice" className="space-y-4 pt-3">
        <VoiceCapture autoStartKey={captureVoiceRequestId} onComplete={onDone} />
      </TabsContent>

      <TabsContent value="notes" className="space-y-4 pt-3">
        <form onSubmit={handleNotesSubmit} className="space-y-4">
          <textarea
            value={notesInput}
            onChange={(e) => setNotesInput(e.target.value)}
            placeholder="Paste messy notes here..."
            rows={5}
            className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-high px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-primary/50 focus:outline-none"
            disabled={notesReviewMutation.isPending}
          />
          {notesError && <p className="text-xs text-error">{notesError}</p>}
          <Button type="submit" disabled={(!notesInput.trim() && !notesFile) || notesReviewMutation.isPending} className="w-full">
            {notesReviewMutation.isPending ? 'Preparing...' : 'Start Review'}
          </Button>
        </form>
      </TabsContent>

      <TabsContent value="text" className="space-y-4 pt-3">
        <form onSubmit={handleTextSubmit} className="space-y-4">
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Paste or type text to capture..."
            rows={5}
            className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-high px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-primary/50 focus:outline-none"
            disabled={textMutation.isPending}
          />
          <Button type="submit" disabled={!textInput.trim() || textMutation.isPending} className="w-full">
            {textMutation.isPending ? 'Processing...' : 'Capture Text'}
          </Button>
        </form>
      </TabsContent>

      <TabsContent value="url" className="space-y-4 pt-3">
        <form onSubmit={handleUrlSubmit} className="space-y-4">
          <textarea
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="Paste URLs to save..."
            rows={4}
            className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-high px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-primary/50 focus:outline-none"
            disabled={bulkUrlMutation.isPending}
          />
          {urlError && <p className="text-xs text-error">{urlError}</p>}
          <Button type="submit" disabled={!urlInput.trim() || bulkUrlMutation.isPending} className="w-full">
            {bulkUrlMutation.isPending ? 'Queueing...' : 'Queue URLs'}
          </Button>
        </form>
      </TabsContent>

      <TabsContent value="file" className="space-y-4 pt-3">
        <div
          className={cn(
            'rounded-lg border-2 border-dashed p-6 text-center transition-colors',
            isDragging ? 'border-primary bg-primary/5' : 'border-outline-variant/30',
          )}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input ref={fileInputRef} type="file" onChange={handleFileInputChange} className="sr-only" accept="image/*,audio/*,video/*,.pdf,.md,.markdown,.txt" />
          {!selectedFile ? (
            <div className="space-y-2">
              <span className="material-symbols-outlined text-3xl text-on-surface-variant/50">upload_file</span>
              <p className="text-sm text-on-surface-variant">Drop a file or <button type="button" onClick={() => fileInputRef.current?.click()} className="text-primary underline">browse</button></p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium text-on-surface">{selectedFile.name}</p>
              <p className="text-xs text-on-surface-variant">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              <button type="button" onClick={() => setSelectedFile(null)} className="text-xs text-error underline">Remove</button>
            </div>
          )}
        </div>
        <Button type="button" onClick={handleFileUpload} disabled={!selectedFile || fileMutation.isPending} className="w-full">
          {fileMutation.isPending ? 'Uploading...' : 'Upload File'}
        </Button>
      </TabsContent>
    </Tabs>
  );
}
